import { motion } from 'framer-motion';
import { FiLinkedin, FiMail, FiMapPin, FiPhone } from 'react-icons/fi';
import SectionHeading from './SectionHeading';
import { SiteConfig } from '../types/portfolio';
import Icon from './Icon';

type ContactSectionProps = {
  siteConfig: SiteConfig;
};

const iconMap = {
  Email: FiMail,
  Phone: FiPhone,
  Location: FiMapPin,
  LinkedIn: FiLinkedin,
};

function ContactSection({ siteConfig }: ContactSectionProps) {
  return (
    <section id="contact" className="section-shell pb-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          eyebrow="Contact"
          title="Open to engineering conversations, enterprise roles, and impactful product work."
          description="Reach out for senior Java full stack opportunities, backend platform work, or large-scale enterprise application development."
        />

        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="glass-panel rounded-[2rem] p-8"
          >
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-sky-200">
              Let&apos;s connect
            </p>
            <h3 className="mt-4 text-3xl font-semibold text-white">
              Senior Java engineering with modern frontend execution.
            </h3>
            <p className="mt-5 max-w-2xl text-base leading-8 text-slate-300">
              Based in Bangalore and focused on enterprise-grade software systems, Sushil brings a combination of backend architecture strength, UI delivery capability, and dependable cross-team execution.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a href={`mailto:${siteConfig.email}`} className="primary-button">
                <Icon icon={FiMail} />
                Email Sushil
              </a>
              <a
                href={siteConfig.linkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="secondary-button"
              >
                <Icon icon={FiLinkedin} />
                View LinkedIn
              </a>
            </div>
          </motion.article>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid gap-4"
          >
            {siteConfig.contactMethods.map((item) => {
              const MethodIcon = iconMap[item.label as keyof typeof iconMap] || FiMail;
              const content = (
                <div className="glass-panel rounded-[1.75rem] p-6 transition hover:border-sky-400/30 hover:bg-sky-400/10">
                  <div className="flex items-center gap-3 text-sky-200">
                    <Icon icon={MethodIcon} />
                    <span className="text-sm font-semibold uppercase tracking-[0.28em]">
                      {item.label}
                    </span>
                  </div>
                  <p className="mt-4 text-base font-medium text-white">{item.value}</p>
                </div>
              );

              return item.href ? (
                <a key={item.label} href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel={item.href.startsWith('http') ? 'noreferrer' : undefined}>
                  {content}
                </a>
              ) : (
                <div key={item.label}>{content}</div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default ContactSection;
